import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { FormlyMatToggleModule } from '@ngx-formly/material/toggle';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DragDropModule } from '@angular/cdk/drag-drop';

// Third-party
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// Custom types
import { SearchableSelectType } from './types/searchable-select.type';
import { TimeRangePickerType } from './types/time-range-picker.type';
import { GoogleAddressType } from './types/google-address.type';
import { CurrencyInputType } from './types/currency-input.type';
import { PhoneInputType } from './types/phone-input.type';
import { RepeatingSectionType } from './types/repeating-section.type';
import { RepeatingECSectionType } from './types/repeating-ec-section.type';
import { FormlyLabelType } from './types/formly-label.type';
import { SinInputType, sinValidator } from './types/sin-input.type';
import { CollectorSelectorType } from './types/collector-selector.type';
import { SectionHeaderType } from './types/section-header.type';
import { FormlyFieldStepper } from './types/stepper.type';
import { FormlyFieldTabs } from './types/tabs.type';
import { MultiCheckboxType } from './types/multi-checkbox.type';
import { AgreementCurrencyDisplayComponent } from './types/agreement/agreement-currency.type';
import { RecipientListType } from './types/agreement/recipient-list.type';

// Wrappers
import { ExpansionPanelWrapper } from './wrappers/expansion-panel.wrapper';
import { PostalCodeInputType } from '@lmc-app/shared/formly/types/postal-code-input.type';
import { MissingValueType } from './types/agreement/missing-value.type';
import { FormlyFieldButton } from './types/formly-field-button.type';
import { CalculatorButtonType } from './types/agreement/calculator-button.type';
import { FormlyTableTypeComponent } from './types/agreement/formly-table.type';
import { AgreementLppValueType } from './types/agreement/agreement-lpp.type';
import { RepeatingAdditionalDebtType } from './types/agreement/repeating-additional-debt.type';
import { YesNoToggleType } from './types/yes-no-toggle.type';
import { PanelWrapper } from './wrappers/panel.wrapper';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

const TRACK_BY_INDEX = { props: { trackByIndex: true } };

@NgModule({
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		MatIconModule,
		MatCardModule,
		MatTooltipModule,
		MatExpansionModule,
		MatDividerModule,
		MatStepperModule,
		MatCheckboxModule,
		MatTabsModule,
		DragDropModule,
		NgxMaskDirective,
		NgxMaskPipe,
		NgxMatSelectSearchModule,
		TimeRangePickerType,
		GoogleAddressType,
		MatSlideToggleModule,
		FormlyModule.forRoot({
			types: [
				{ name: 'postal-code', component: PostalCodeInputType },
				{ name: 'searchable-select', component: SearchableSelectType },
				{ name: 'time-range-picker', component: TimeRangePickerType },
				{ name: 'google-address', component: GoogleAddressType },
				{ name: 'currency', component: CurrencyInputType },
				{ name: 'phone', component: PhoneInputType },
				// Array types rebuild rows by position, as before formly 11
				{
					name: 'repeat',
					component: RepeatingSectionType,
					defaultOptions: TRACK_BY_INDEX,
				},
				{
					name: 'repeat-ec',
					component: RepeatingECSectionType,
					defaultOptions: TRACK_BY_INDEX,
				},
				{ name: 'formly-label', component: FormlyLabelType },
				{ name: 'sin', component: SinInputType },
				{ name: 'collector-selector', component: CollectorSelectorType },
				{ name: 'section-header', component: SectionHeaderType },
				{ name: 'stepper', component: FormlyFieldStepper, wrappers: [] },
				{ name: 'tabs', component: FormlyFieldTabs, wrappers: [] },
				{ name: 'missing-value', component: MissingValueType },
				{ name: 'button', component: FormlyFieldButton },
				{ name: 'multi-checkbox', component: MultiCheckboxType },
				{ name: 'calc-button', component: CalculatorButtonType },
				{
					name: 'table',
					component: FormlyTableTypeComponent,
					defaultOptions: TRACK_BY_INDEX,
				},
				{
					name: 'agreement-currency',
					component: AgreementCurrencyDisplayComponent,
				},
				{ name: 'agreement-lpp-value', component: AgreementLppValueType },
				{ name: 'recipient-list', component: RecipientListType },
				{
					name: 'repeat-additional-debt',
					component: RepeatingAdditionalDebtType,
					defaultOptions: TRACK_BY_INDEX,
				},
				{ name: 'yes-no-toggle', component: YesNoToggleType },
			],
			wrappers: [
				{ name: 'expansion-panel', component: ExpansionPanelWrapper },
				{ name: 'panel', component: PanelWrapper },
			],
			validators: [{ name: 'sin', validation: sinValidator }],
			validationMessages: [
				{ name: 'required', message: 'Required' },
				{ name: 'email', message: 'Invalid email address' },
				{ name: 'pattern', message: 'Invalid format' },
				{ name: 'sin', message: 'Invalid SIN number' },
				// control.setErrors({ server: message }) is how a dialog puts a server-side field
				// rejection back on its control (e.g. promise-to-pay-dialog.component.ts). Without
				// this, mat-error still shows (formControl.invalid is true) but empty — nothing
				// resolved the "server" error key to text.
				{ name: 'server', message: (error: string) => error },
			],
			extras: {
				lazyRender: true,
				// checkExpressionOn: 'changeDetectionCheck',
			},
		}),
		FormlyMaterialModule,
		FormlyMatDatepickerModule,
		FormlyMatToggleModule,
		PostalCodeInputType,
		SearchableSelectType,
		CurrencyInputType,
		PhoneInputType,
		RepeatingSectionType,
		RepeatingECSectionType,
		FormlyLabelType,
		ExpansionPanelWrapper,
		SinInputType,
		CollectorSelectorType,
		SectionHeaderType,
		FormlyFieldStepper,
		MultiCheckboxType,
		FormlyFieldTabs,
		MissingValueType,
		CalculatorButtonType,
		RecipientListType,
		RepeatingAdditionalDebtType,
		YesNoToggleType,
		PanelWrapper,
	],
	exports: [FormlyModule, FormlyMaterialModule],
	providers: [provideNgxMask()],
})
export class AppFormlyModule {}
